import React, { useState, useEffect } from "react";
import { CheckCircle, Award, Users, Clock } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import { getCompanyInfo } from "../services/api";

const AboutPage = () => {
  const [companyInfo, setCompanyInfo] = useState({
    phone: "1-888-907-3777"
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const companyInfoData = await getCompanyInfo();
        setCompanyInfo(companyInfoData);
      } catch (error) {
        console.error("Error fetching company info:", error);
      }
    };

    fetchData();
  }, []);

  return (
    <div>
      <SEO 
        title="About Groundhogs Septic | Thunder Bay Experts"
        description="Thunder Bay&apos;s trusted septic installation experts. Over 15 years experience, 1,200+ installations, 100% permit approval rate. Family-owned and operated."
        keywords="about Groundhogs Septics, Thunder Bay septic company, septic installation experts"
      />
      <Header />

      {/* Hero Section */}
      <section className="gh-intro pt-32 pb-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="gh-intro-heading text-center mb-12" data-reveal>
            <h1 className="text-5xl font-bold mb-4" style={{ color: "var(--dark-navy)" }}>
              About Groundhogs™ Environmental
            </h1>
            <p className="text-xl" style={{ color: "var(--primary-orange)" }}>
              Northwestern Ontario&apos;s Trusted Septic Installation Experts
            </p>
          </div>
        </div>
      </section>

      {/* Company Story */}
      <section className="py-16" style={{ backgroundColor: "var(--light-gray)" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-6" style={{ color: "var(--dark-navy)" }}>
                Our Story
              </h2>
              <p className="mb-4" style={{ color: "var(--text-dark)" }}>
                Groundhogs™ Environmental - Septic Solutions has grown to become Thunder Bay and Northwestern Ontario&apos;s 
                most trusted name in septic system installation. What started as a focused, family-operated business 
                has evolved into the region&apos;s premier septic installation company.
              </p>
              <p className="mb-4" style={{ color: "var(--text-dark)" }}>
                With over 1,200 successful installations across Northwestern Ontario, we&apos;ve built our reputation on 
                precision, reliability, and results that stand the test of time. Our team specializes in both traditional 
                septic systems and advanced Eljen mound systems, engineered specifically for our region&apos;s unique soil 
                conditions and challenging climate.
              </p>
              <p style={{ color: "var(--text-dark)" }}>
                Headquartered in Thunder Bay, we serve homeowners and businesses throughout the region with the same 
                commitment to quality, professionalism, and customer satisfaction that has defined us from day one.
              </p>
            </div>
            <div className="rounded-lg overflow-hidden shadow-xl">
              <img 
                src="/seo-images/4z8mh8az_unnamed.webp" 
                alt="Groundhogs™ Environmental septic installation crew at work in Thunder Bay, Northwestern Ontario"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-center" style={{ color: "var(--dark-navy)" }}>
            Why Choose Groundhogs™ Environmental?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ backgroundColor: "var(--primary-orange)" }}>
                  <Award size={32} className="text-white" />
                </div>
              </div>
              <h3 className="text-xl font-bold mb-3" style={{ color: "var(--dark-navy)" }}>
                Eljen Certified
              </h3>
              <p style={{ color: "var(--text-light)" }}>
                Factory-trained and certified installers of advanced Eljen mound systems
              </p>
            </div>
            <div className="text-center">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ backgroundColor: "var(--primary-orange)" }}>
                  <CheckCircle size={32} className="text-white" />
                </div>
              </div>
              <h3 className="text-xl font-bold mb-3" style={{ color: "var(--dark-navy)" }}>
                100% Permit Success
              </h3>
              <p style={{ color: "var(--text-light)" }}>
                Expert engineering partners with expedited permit processing - even MOE approvals
              </p>
            </div>
            <div className="text-center">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ backgroundColor: "var(--primary-orange)" }}>
                  <Clock size={32} className="text-white" />
                </div>
              </div>
              <h3 className="text-xl font-bold mb-3" style={{ color: "var(--dark-navy)" }}>
                3-Day Average Installation
              </h3>
              <p style={{ color: "var(--text-light)" }}>
                Systemized process - most installations completed in about 3 days on average, not weeks
              </p>
            </div>
            <div className="text-center">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ backgroundColor: "var(--primary-orange)" }}>
                  <Users size={32} className="text-white" />
                </div>
              </div>
              <h3 className="text-xl font-bold mb-3" style={{ color: "var(--dark-navy)" }}>
                Expert Team
              </h3>
              <p style={{ color: "var(--text-light)" }}>
                10+ skilled technicians committed to quality and professionalism
              </p>
            </div>
            <div className="text-center">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ backgroundColor: "var(--primary-orange)" }}>
                  <CheckCircle size={32} className="text-white" />
                </div>
              </div>
              <h3 className="text-xl font-bold mb-3" style={{ color: "var(--dark-navy)" }}>
                99% Satisfaction
              </h3>
              <p style={{ color: "var(--text-light)" }}>
                Outstanding customer satisfaction backed by 5-year warranty
              </p>
            </div>
          </div>
      {/* Our Septic Crews */}
      <section className="py-16" style={{ backgroundColor: "var(--light-gray)" }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2
            className="text-4xl font-bold mb-2 text-center"
            style={{ color: "var(--dark-navy)" }}
          >
            Our Septic Crews – Leaders, Not Just Operators
          </h2>
          <p
            className="text-lg font-semibold mb-6 text-center"
            style={{ color: "var(--text-dark)" }}
          >
            Cross-Trained. Multi-Disciplined. Proven in Northern Ontario Conditions.
          </p>
          <div className="space-y-4 text-base" style={{ color: "var(--text-dark)" }}>
            <p>
              The people who work at Groundhogs&trade; Septic aren&apos;t ordinary labourers – they&apos;re leaders
              in their trades. Our crews are cross-trained specialists who bring expertise across excavation,
              heavy equipment operation, on-site wastewater design, site grading, and advanced septic
              technologies like Eljen GSF.
            </p>
            <p>
              Every crew member at Groundhogs&trade; has been rigorously trained and field-tested. We hold each
              other to an exceptionally high standard – many who apply here don&apos;t make it. But the ones who
              stay? They&apos;re elite septic professionals who understand both the practical and technical sides of
              on-site sewage systems.
            </p>
            <p>
              Our septic installation technicians aren&apos;t just skilled with their hands – they understand
              how soil types, groundwater, bedrock, system sizing, and Ontario Building Code requirements all
              work together. They can read engineered drawings, coordinate with inspectors and designers, and
              solve complex installation challenges in real time on clay, rock, and high-water-table sites
              across Northwestern Ontario.
            </p>
            <p>
              This isn&apos;t just a job for them. It&apos;s a craft. And Groundhogs&trade; Septic is a proud place to
              work for people who take pride in doing things properly the first time.
            </p>
            <p>
              We&apos;re not just installing septic systems – we&apos;re raising the standard for what skilled trades
              can achieve in on-site wastewater across Northern Ontario.
            </p>
          </div>
        </div>
      </section>

        </div>
      </section>

      {/* Certifications */}
      <section className="py-16" style={{ backgroundColor: "var(--light-gray)" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-center" style={{ color: "var(--dark-navy)" }}>
            Certifications & Compliance
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-md">
              <h3 className="text-2xl font-bold mb-4" style={{ color: "var(--dark-navy)" }}>
                Eljen Certified Installer
              </h3>
              <p style={{ color: "var(--text-light)" }}>
                Factory-trained and certified to install Eljen GSF (Geotextile Sand Filter) mound systems to 
                manufacturer specifications.
              </p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-md">
              <h3 className="text-2xl font-bold mb-4" style={{ color: "var(--dark-navy)" }}>
                Expert Permit Processing
              </h3>
              <p style={{ color: "var(--text-light)" }}>
                Partnered with Thunder Bay&apos;s top septic engineers for expedited permit processing. 100% approval success rate 
                including complex Ministry of Environment applications. All installations exceed Ontario Building Code requirements.
              </p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-md">
              <h3 className="text-2xl font-bold mb-4" style={{ color: "var(--dark-navy)" }}>
                Fully Insured
              </h3>
              <p style={{ color: "var(--text-light)" }}>
                Comprehensive liability insurance and WSIB coverage protects you and your property throughout 
                the installation process.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Commitment */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6" style={{ color: "var(--dark-navy)" }}>
            Our Commitment to You
          </h2>
          <p className="text-lg mb-8" style={{ color: "var(--text-dark)" }}>
            At Groundhogs™ Environmental, we don&apos;t just install septic systems – we build lasting relationships with 
            our clients. Every project receives the same attention to detail, professionalism, and commitment to 
            quality that has made us Northwestern Ontario&apos;s most trusted septic installation company.
          </p>
          <ul className="text-left max-w-2xl mx-auto space-y-3 mb-8">
            <li className="flex items-start space-x-3">
              <CheckCircle size={24} style={{ color: "var(--primary-orange)" }} className="flex-shrink-0 mt-1" />
              <span>Transparent pricing with detailed written estimates</span>
            </li>
            <li className="flex items-start space-x-3">
              <CheckCircle size={24} style={{ color: "var(--primary-orange)" }} className="flex-shrink-0 mt-1" />
              <span>Clear communication throughout the entire process</span>
            </li>
            <li className="flex items-start space-x-3">
              <CheckCircle size={24} style={{ color: "var(--primary-orange)" }} className="flex-shrink-0 mt-1" />
              <span>Clean, organized worksites with minimal disruption</span>
            </li>
            <li className="flex items-start space-x-3">
              <CheckCircle size={24} style={{ color: "var(--primary-orange)" }} className="flex-shrink-0 mt-1" />
              <span>Complete permit handling and inspection coordination</span>
            </li>
            <li className="flex items-start space-x-3">
              <CheckCircle size={24} style={{ color: "var(--primary-orange)" }} className="flex-shrink-0 mt-1" />
              <span>5-year warranty on all installations</span>
            </li>
          </ul>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;